import Navbar from './Navbar';
import Footer from './Footer';
import { useState } from 'react';
import { InfinitySpin } from 'react-loader-spinner';
import { Lock, User, Eye, EyeOff, Shield } from 'react-feather';
import axios from 'axios';

function AdminLogin() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // API proxied through Nginx - uses same domain and SSL cert
  const api_url = window.location.origin;

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${api_url}/api/admin/login`, {
                username: username,
                password: password
            });

            if (response.status === 200) {
                setSuccess(true);
                // Store admin token if provided
                if (response.data.token) {
                    localStorage.setItem('adminToken', response.data.token);
                }
                // Redirect to admin dashboard
                setTimeout(() => {
                    window.location.href = '/admin/dashboard';
                }, 2000);
            }
        } catch (error) {
            console.error('Login error:', error);
            setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    // Render form content directly instead of using a nested component
    const renderFormContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <InfinitySpin color="#64a19d" />
                    <p style={{ marginTop: '20px', color: '#64748b' }}>Authenticating...</p>
                </div>
            );
        }

        if (success) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#10b981',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        <Shield size={24} color="white" />
                    </div>
                    <h4 style={{ color: '#10b981', marginBottom: '10px' }}>Login Successful!</h4>
                    <p style={{ color: '#64748b' }}>Redirecting to admin dashboard...</p>
                </div>
            );
        }

        return (
            <form onSubmit={handleLogin}>
                {error && (
                    <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}
                
                <div className="mb-4">
                    <label className="small text-gray-600 mb-2 d-block">
                        <User size={14} style={{ marginRight: '4px' }} />
                        Username
                    </label>
                    <input 
                        type="text"
                        className="form-control" 
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your username"
                        required
                        style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '14px'
                        }}
                    />
                </div>

                <div className="mb-4">
                    <label className="small text-gray-600 mb-2 d-block">
                        <Lock size={14} style={{ marginRight: '4px' }} />
                        Password
                    </label>
                    <div style={{ position: 'relative' }}>
                        <input 
                            type={showPassword ? "text" : "password"}
                            className="form-control" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            required
                            style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                paddingRight: '50px',
                                fontSize: '14px'
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                color: '#64748b',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                </div>

                <button 
                    type="submit"
                    className="btn btn-primary w-100" 
                    disabled={!username || !password}
                    style={{
                        backgroundColor: '#64a19d',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(100, 161, 157, 0.3)'
                    }}
                >
                    <Lock size={16} style={{ marginRight: '8px' }} />
                    Sign In
                </button>
            </form>
        );
    };

    return (
        <>
            <Navbar />
            <div id="layoutDefault">
                <div id="layoutDefault_content">
                    <main>
                        <header className="page-header-ui page-header-ui-dark bg-gradient-primary-to-secondary">
                            <div className="page-header-ui-content pt-10">
                                <div className="container px-5 text-center">
                                    <div className="row gx-5 justify-content-center">
                                        <div className="col-lg-8">
                                            <div className="badge badge-marketing rounded-pill bg-secondary-soft text-secondary mb-3">
                                                <Shield size={16} style={{ marginRight: '6px' }} />
                                                Admin Access
                                            </div>
                                            <h1 className="page-header-ui-title mb-3">Admin Login</h1>
                                            <p className="page-header-ui-text">Secure access to administrative functions</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="svg-border-rounded text-light">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 144.54 17.34" preserveAspectRatio="none" fill="currentColor">
                                    <path d="M144.54,17.34H0V0H144.54ZM0,0S32.36,17.34,72.27,17.34,144.54,0,144.54,0"></path>
                                </svg>
                            </div>
                        </header>
                        
                        <section className="bg-light py-10">
                            <div className="container px-5">
                                <div className="row gx-5 justify-content-center">
                                    <div className="col-lg-6 col-xl-5">
                                        <div className="card rounded-3 text-dark" style={{ 
                                            border: 'none', 
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                            borderRadius: '20px'
                                        }}>
                                            <div className="card-header py-4" style={{ 
                                                backgroundColor: '#f8fafc', 
                                                borderBottom: '1px solid #e2e8f0',
                                                borderRadius: '20px 20px 0 0',
                                                textAlign: 'center',
                                                fontWeight: '600',
                                                fontSize: '18px',
                                                color: '#1e293b'
                                            }}>
                                                <Shield size={20} style={{ marginRight: '8px', color: '#64a19d' }} />
                                                Administrator Access
                                            </div>
                                            <div className="card-body p-4">
                                                {renderFormContent()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default AdminLogin; 